import { useState, useCallback } from 'react';
import { apiClient } from '../api/client';

export interface UseFormSubmissionOptions<TInput> {
  endpoint: string;
  onSuccess?: (submissionId: string) => void;
  onError?: (error: Error) => void;
}

export function useFormSubmission<TInput>({
  endpoint,
  onSuccess,
  onError,
}: UseFormSubmissionOptions<TInput>) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submit = useCallback(
    async (input: TInput) => {
      setIsSubmitting(true);
      setError(null);

      let lastError: Error | null = null;

      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const res = await apiClient.post(endpoint, input);
          const id = res.data?.data?.id || res.data?.id || 'submitted';
          setIsSubmitting(false);
          onSuccess?.(id);
          return;
        } catch (err: any) {
          lastError = err instanceof Error ? err : new Error(err?.response?.data?.error || 'Submission failed');
          // Only retry on network / 5xx errors
          const status = err?.response?.status;
          if (status && status >= 400 && status < 500) break;
        }
      }

      setError(lastError);
      setIsSubmitting(false);
      onError?.(lastError!);
    },
    [endpoint, onSuccess, onError],
  );

  return { submit, isSubmitting, error };
}
